/*  MUI  */
import { Box, Table, TableBody, TableContainer } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';

/*  Shared  */
import { TRANSLATERISKS } from 'application/Shared/Constants';
import { Helpers } from 'application/Shared/Helpers';
import { DataAPI } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/*  React  */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';

/* Internal Components */
import { ManagedTableCSVExport } from './internal/ManagedTableCSVExport';
import { ManagedTableFooter } from './internal/ManagedTableFooter';
import { ManagedTableHeaders } from './internal/ManagedTableHeaders';
import { ManagedTableNewData } from './internal/ManagedTableNewData';
import { ManagedTableRows } from './internal/ManagedTableRows';
import { ManagedTableSearchBar } from './internal/ManagedTableSearchBar';
import { ManagedTableSkeletonRows } from './internal/ManagedTableSkeletonRows';
import { ManagedTableToggles } from './internal/ManagedTableToggles';

/*  Constants  */
const { isset } = Helpers;

const VALID_ROWS_PER_PAGE = [-1, 2, 5, 10, 25, 50, 100];

const getParameters = ({
    clientId,
    currentDataAPI,
    filters,
    parameters,
    toggles
}) => {
    if (clientId) {
        currentDataAPI.addParameter('platformTenantId', clientId);
        currentDataAPI.addParameter('includeClients', true);
    }

    // For all the toggled checkboxes, add their parameters to the api call
    Object.values(toggles).forEach((toggle) => {
        const { enabled, parameter } = toggle;
        if (enabled) {
            const { field, value } = parameter;
            currentDataAPI.addParameter(field, value);
        }
    });

    // For all the toggled filters in the header column, add their parameters to the api call
    if (Object.keys(filters)?.length > 0) {
        Object.values(filters).forEach((filter) => {
            if (filter.values?.length) {
                const { parameter, visualValues } = filter;
                const values = filter.values.filter(
                    (v) => !visualValues?.includes(v)
                );

                // Values could have been filtered above, and if so, we don't need to send an empty param.
                if (values?.length) {
                    currentDataAPI.addParameter(parameter, values);
                }
            }
        });
    }

    // If there are any parameters add those last.
    if (Helpers.isObject(parameters)) {
        Object.keys(parameters).forEach((field) => {
            const value = parameters[field];
            currentDataAPI.addParameter(field, value);
        });
    }

    // Get the built config and return the parameters
    return currentDataAPI.getConfig().parameters;
};

const handleRowSorting = ({ currentRowData, sortState }) => {
    const isDate = sortState.fieldType === 'date';
    const isNumber = sortState.fieldType === 'number';
    const isRisk = sortState.fieldType === 'risk';
    const isString = sortState.fieldType === 'string';

    const translateRisks = TRANSLATERISKS;
    const currentRenderRows = currentRowData.sort((a, b) => {
        let valA;
        let valB;

        try {
            if (sortState.key.indexOf('.') > -1) {
                valA = sortState.key
                    .split('.')
                    .reduce((value1, value2) => value1?.[value2], a);
                valB = sortState.key
                    .split('.')
                    .reduce((value1, value2) => value1?.[value2], b);
            } else if (sortState.key.indexOf(',') > -1) {
                // Adding a comma to the key allows us to sort by a second key if the first value is null
                const sortKeys = sortState.key.split(',');
                sortKeys.forEach((keyVal) => {
                    valA = valA ?? a[keyVal];
                    valB = valB ?? b[keyVal];
                });
            } else {
                valA = a[sortState.key];
                valB = b[sortState.key];
            }
        } catch (e) {
            valA = '';
            valB = '';
        }

        if (!isDate && !isNumber && !isRisk && !isString) {
            debug.e(
                'sortFieldType needs to be string, date, number, or risk, current: ',
                sortState.fieldType
            );
        }

        if (isDate) {
            const tempA = Date.parse(valA);
            const tempB = Date.parse(valB);
            /* If a date does not exist, set the date value for sorting
            into the year 0 so it always shows up last */
            valA = Number.isNaN(tempA) ? 0 : tempA;
            valB = Number.isNaN(tempB) ? 0 : tempB;
        } else if (isRisk) {
            valA = translateRisks[valA];
            valB = translateRisks[valB];
        } else if (isNumber) {
            valA = Number.isNaN(parseInt(valA, 10)) ? null : parseInt(valA, 10);
            valB = Number.isNaN(parseInt(valB, 10)) ? null : parseInt(valB, 10);
        } else if (
            isString &&
            typeof valA === 'string' &&
            typeof valB === 'string'
        ) {
            valA = valA.toLowerCase();
            valB = valB.toLowerCase();
        }

        if (valA === valB) {
            return 0;
        }
        // null values sort after anything else
        if (valA == null) {
            return 1;
        }
        if (valB == null) {
            return -1;
        }
        return (
            (valA > valB ? 1 : -1) * (sortState.direction === 'ASC' ? 1 : -1)
        );
    });

    return currentRenderRows;
};

const handleSearching = ({ currentRenderRows, searchFields, searchText }) =>
    currentRenderRows.filter((row) => {
        if (searchText === '') {
            return true;
        }

        return searchFields.reduce((bool, field) => {
            if (bool) return bool;

            // For fields like application.name
            let compareValue;
            if (field.indexOf('.') > -1) {
                compareValue = field.split('.').reduce((a, b) => a?.[b], row);
            } else {
                compareValue = row[field];
            }
            if (typeof compareValue === 'undefined') {
                debug.dev(`Search field ${field} does not exist`);
                return false;
            }

            if (compareValue === null) {
                return false;
            }

            if (typeof compareValue !== 'string') {
                /* debug.e(
                    `compareValue on '${field}' should be a string. Its type is ${typeof compareValue}`,
                    compareValue
                ); */
                return true;
            }

            return (
                bool ||
                (compareValue !== null &&
                    compareValue.toLowerCase().indexOf(searchText) > -1)
            );
        }, false);
    });

const makeToggles = (propsToggles) => {
    const currentToggles = {};

    if (propsToggles?.length) {
        propsToggles.forEach((toggle) => {
            const { key, defaultEnabled } = toggle;
            currentToggles[key] = { ...toggle, enabled: defaultEnabled };
        });
    }

    return currentToggles;
};

const setupFilters = (propsHeaders) => {
    const currentFilters = {};

    propsHeaders?.forEach((propHeader) => {
        const { filterableOptions, filterable } = propHeader;

        if (filterable && filterableOptions) {
            filterableOptions.forEach((option) => {
                const { parameter, value, visualOnly } = option;

                // If the key is not set, put the default value there
                if (!isset(currentFilters, parameter)) {
                    currentFilters[parameter] = {
                        parameter,
                        showValues: [],
                        values: [],
                        visualValues: []
                    };
                }

                if (!currentFilters[parameter].showValues.includes(value)) {
                    currentFilters[parameter].showValues.push(value);
                }

                // Push the value of the default filter
                if (
                    option.default &&
                    !currentFilters[parameter].values.includes(value)
                ) {
                    currentFilters[parameter].values.push(value);
                }
                if (
                    visualOnly &&
                    !currentFilters[parameter].visualValues.includes(value)
                ) {
                    currentFilters[parameter].visualValues.push(value);
                }
            });
        }
    });

    return currentFilters;
};

const setupColSpan = ({ handleSelectAll, propsHeaders }) => {
    let colSpan = 0;

    propsHeaders?.forEach((propHeader) => {
        colSpan += propHeader.colSpan ? propHeader.colSpan : 1;
    });

    // We eventually inject a new header cell if we have a select all checkbox at the top (handleSelectAll).
    // Therefore, we need to increase the number of columns shown in the skeleton rows and in the empty message by one.
    return handleSelectAll ? colSpan + 1 : colSpan;
};

const setupHeaders = (propsHeaders) => {
    const currentHeaders = [];
    propsHeaders?.forEach((propHeader, index) => {
        const { field } = propHeader;

        currentHeaders.push({
            ...propHeader,
            // TODO: Come up with a better "index" for the key
            key: `table_header_${field}_${index}`
        });
    });
    return currentHeaders;
};

const setupDataAPI = ({
    apiSize,
    clientId,
    dataAPI,
    filters,
    page,
    parameters,
    rowsApi,
    rowsApiPreload,
    rowsPerPage,
    sortState,
    toggles
}) => {
    // If we are setting up dataAPI, we need to clean up the previous parameters
    dataAPI.resetParams();

    /* Get any query parameters that may have been sent with the route
        '/{api}/?enabled=true' and add them as values to dataAPI */
    const params = Helpers.getQueryParamsFromString(rowsApi);
    dataAPI.addParameters(params);

    const newParams = getParameters({
        clientId,
        currentDataAPI: dataAPI,
        filters,
        parameters,
        toggles
    });
    dataAPI.addParameters(newParams);

    // If not doing pagination, set the size to -1
    if (rowsApiPreload) {
        dataAPI.addParameter('size', apiSize || -1);
    } else {
        dataAPI.addParameters({
            order: sortState.direction,
            page: page + 1,
            size: rowsPerPage,
            sort: sortState.key
        });
    }
};

const queryCountApi = async ({ countAPI, dataAPI, signal }) => {
    // countAPI's params need to be in sync with dataAPI's
    // eslint-disable-next-line no-param-reassign
    countAPI.config = dataAPI.getConfig();
    // take the original queries config, then add count to true
    countAPI.addParameter('count', true);

    // get the response and assign the count
    const rowCountRes = await countAPI.getResults(signal);
    const currentRowCount = isset(rowCountRes, 'count')
        ? parseInt(rowCountRes.count, 10)
        : null;

    return currentRowCount;
};

/**
 * A reusable table component that handles data loading, sorting, filtering, pagination and more
 * @component
 * @param {Object} props - Component props
 * @param {boolean} [props.alternateRows] - Style the table with alternate row colors
 * @param {number} [props.apiSize] - Number of records to request from backend
 * @param {boolean} [props.autoupdate] - Reload table data every 30 seconds
 * @param {string} [props.className] - CSS class name - Used in two places for `squeezedTable`
 * @param {boolean} [props.csvBuildOnRequest] - Build CSV data on export request
 * @param {Array} [props.csvData] - Data to use for CSV export
 * @param {boolean} [props.csvExport] - Enable CSV export functionality
 * @param {Array} [props.csvExportFields] - Fields to include in CSV export
 * @param {string} [props.csvExportFileName] - Filename for CSV export
 * @param {boolean} [props.csvIconOnly] - Show only CSV icon without text
 * @param {string} [props.data-trackid] - Data tracking ID
 * @param {string} [props.defaultSearch] - Initial search text
 * @param {boolean} [props.disableFirstAndLastPageButtons] - Disable first/last pagination buttons - useful for cases where we don't know how to display the last page, such as in Provider groups.
 * @param {string} [props.emptyRowsMessage] - Message to show when table is empty
 * @param {boolean} [props.externalLoading] - Boolean - used to know if we are still loading data in the component using ManagedTable.
 * @param {ReactNode} [props.footer] - Footer component
 * @param {Function} [props.handleRows] - Callback for processing row data in the component calling ManagedTable
 * @param {Function} [props.handlePageChange] - Callback when page changes - for when we need to know the current page
 * @param {Function} [props.handleRowsPerPage] - Callback when rows per page changes
 * @param {Function} [props.handleSelectAll] - Callback for select all functionality
 * @param {Array} [props.headers] - Table header configuration
 * @param {boolean} [props.manageRows] - If true, rows are pre-wrapped in TableRow
 * @param {number} [props.maxExportRows] - Maximum rows for CSV export
 * @param {ReactNode} [props.noResultsRender] - Component to render when no results - replaces table
 * @param {Object} [props.parameters] - Additional API parameters
 * @param {Function} [props.preprocessRows] - Function to preprocess row data, e.g., adding properties to the rows array.
 * @param {ReactNode} [props.renderBody] - Component to render table body
 * @param {string} [props.renderBodyKey] - Key for body rendering
 * @param {boolean} [props.renderFooter] - Whether to render footer
 * @param {boolean} [props.renderHeaderRow] - Whether to render header row
 * @param {boolean} [props.rerender] - Force table re-render
 * @param {number} [props.rowHeight] - Height of table rows
 * @param {number} [props.rowCount] - Total row count for pagination
 * @param {Array} [props.rows] - Static row data
 * @param {string} [props.rowsApi] - API endpoint for fetching data
 * @param {boolean} [props.rowsApiPreload] - Load all data at once vs pagination
 * @param {number} [props.rowsPerPage] - Number of rows per page
 * @param {boolean} [props.searchEnabled] - Enable search functionality
 * @param {Array} [props.searchFields] - Fields to include in search
 * @param {Function} [props.setDataFn] - Callback to pass data to parent
 * @param {string} [props.sortDirection] - Initial sort direction
 * @param {string} [props.sortFieldType] - Type of field to sort by
 * @param {string} [props.sortKey] - Key to sort by
 * @param {Array} [props.toggles] - Table toggle configuration
 * @param {boolean} [props.skipSorting=false] - Skip table sorting
 * @param {string|number} [props.width] - Table width
 */
export const ManagedTable = ({
    alternateRows,
    apiSize,
    autoupdate,
    className,
    csvBuildOnRequest,
    csvData,
    csvExport,
    csvExportFields,
    csvExportFileName,
    csvIconOnly,
    'data-trackid': dataTrackid,
    defaultSearch,
    disableFirstAndLastPageButtons,
    emptyRowsMessage,
    externalLoading,
    footer,
    handleRows,
    handlePageChange,
    handleRowsPerPage,
    handleSelectAll,
    headers: propsHeaders,
    manageRows,
    maxExportRows,
    noResultsRender,
    parameters,
    preprocessRows,
    renderBody,
    renderBodyKey,
    renderFooter,
    renderHeaderRow,
    rerender,
    rowHeight,
    rowCount: propsRowCount,
    rows,
    rowsApi,
    rowsApiPreload,
    rowsPerPage: propsRowsPerPage,
    searchEnabled,
    searchFields,
    setDataFn,
    sortDirection: propsSortDirection,
    sortFieldType: propsSortFieldType,
    sortKey: propsSortKey,
    toggles: propsToggles,
    skipSorting = false,
    width
}) => {
    const { partnerId, clientId } = useParams();

    const [apiLoading, setApiLoading] = useState(false);
    const apiTable = !!rowsApi;
    // Adding a function to set state only works the first render, not the next, as it will be ignored.
    // https://react.dev/reference/react/useState#avoiding-recreating-the-initial-state
    const [filters, setFilters] = useState(setupFilters(propsHeaders));
    const [page, setPage] = useState(0);
    const [renderCount, setRenderCount] = useState(0);
    const [renderRows, setRenderRows] = useState(null);
    const [rowCount, setRowCount] = useState(rows?.length || null);
    const [rowData, setRowData] = useState(
        !rowsApi && rows?.length ? [...rows] : null
    );
    const [rowsPerPage, setRowsPerPage] = useState(
        VALID_ROWS_PER_PAGE.includes(propsRowsPerPage) ? propsRowsPerPage : 10
    );
    const [searchText, setSearchText] = useState(
        defaultSearch ? defaultSearch.toLowerCase() : ''
    );
    const [sortState, setSortState] = useState({
        direction: propsSortDirection || 'DESC',
        fieldType: propsSortFieldType || 'string',
        key: propsSortKey || 'id'
    });
    const [cachedData, setCachedData] = useState(null);
    const [queryDataKey, setQueryDataKey] = useState(null);
    const [queryCountKey, setQueryCountKey] = useState(null);
    const [skipCountRows, setSkipCountRows] = useState(false);
    const [newDataAvailable, setNewDataAvailable] = useState(false);
    const [newDataLoading, setNewDataLoading] = useState(false);
    const [isStale, setIsStale] = useState(false);

    const [toggles, setToggles] = useState(makeToggles(propsToggles));

    const colSpan = setupColSpan({ handleSelectAll, propsHeaders });
    const headers = setupHeaders(propsHeaders);
    // rowsApi?.split is used to take the main part of the route.
    // If any parameters are added after the `?`, they will be ignored.
    // If parameters are needed, they need to be added to the parameters object instead.
    const dataAPI = useRef(
        rowsApi
            ? new DataAPI({
                  route: rowsApi?.split('?')[0]
              })
            : null
    );

    const countAPI = useRef(
        rowsApi ? new DataAPI(dataAPI?.current?.getConfig()) : null
    );

    const cancelDataApis = useCallback(
        ({ cancelDataApi = false, cancelCountApi = false }) => {
            if (cancelDataApi && dataAPI.current) {
                dataAPI.current.cancel();
            }
            if (cancelCountApi && countAPI.current) {
                countAPI.current.cancel();
            }
        },
        []
    );

    const queryClient = useQueryClient();

    const fetchData = async (signal) => {
        if (!rowsApi) {
            debug.i('rowsApi is undefined', rowsApi);
            return cachedData;
        }

        if (!dataAPI.current) {
            debug.i('dataAPI has not been initialized');
            return cachedData;
        }

        debug.dev('TABLE API', rowsApi, dataAPI.current.getParams());

        const currentRowData = await dataAPI.current.getResults(signal);

        return currentRowData;
    };

    const fetchQueryCount = async (signal) =>
        queryCountApi({
            countAPI: countAPI.current,
            dataAPI: dataAPI.current,
            signal
        });

    // The queryFn is called when enabled is true and every time the key changes
    // We are cancelling previous calls when calling the queryData. This is why
    // we have two useQuery hooks.
    const queryData = useQuery({
        enabled: !!queryDataKey,
        queryFn: async ({ signal }) => {
            setCachedData([]);
            const response = await fetchData(signal);

            setNewDataLoading(false);

            if (response?.status === 'error') {
                setRowData([]);
                throw new Error(response?.message || 'An error occurred');
            }

            setCachedData(response);
            return response;
        },
        queryKey: queryDataKey,
        refetchInterval: autoupdate ? 30000 : false,
        refetchOnMount: false,
        retry: 0,
        staleTime: Infinity
    });

    const queryRowCount = useQuery({
        enabled: !!queryCountKey,
        queryFn: async ({ signal }) => {
            setRowCount(null);
            const currentRowCount = await fetchQueryCount(signal);
            setRowCount(currentRowCount);
            return currentRowCount;
        },
        queryKey: queryCountKey,
        refetchInterval: autoupdate ? 3000 : false,
        refetchOnMount: false,
        staleTime: Infinity
    });

    const handleRenderRows = useCallback(() => {
        if (!rowData) {
            setRenderCount(0);
            setRenderRows(null);
            return;
        }

        let currentRenderRows = rowData;

        // Allow the parent component to perform any pre-processing of the data
        if (preprocessRows) {
            currentRenderRows = preprocessRows(currentRenderRows);
        }

        // Sorting
        if (
            !skipSorting &&
            sortState.key &&
            sortState.direction &&
            rowData?.length > 0
        ) {
            currentRenderRows = handleRowSorting({
                currentRowData: currentRenderRows,
                sortState
            });
        }

        // Searching
        if (searchEnabled && currentRenderRows?.length > 0) {
            currentRenderRows = handleSearching({
                currentRenderRows,
                searchFields,
                searchText
            });
        }

        if (currentRenderRows?.length === undefined) {
            setRenderCount(0);
            setRenderRows(null);
            return;
        }

        // So that when select all the apps via the select all button at the header, and we search,
        // the actions button will show "Edit n Applications", where n < all apps,
        // and is the number of apps that matched the search, and we only modify that subset of apps, and not all of them.
        if (handleRows && handleSelectAll) {
            handleRows(currentRenderRows);
        }

        const currentRenderCount = propsRowCount || currentRenderRows?.length;

        // Set the render rows to be a subset of rows if we have rowsApiPreload or
        // if we are getting the data/rows from the component itself
        if (rowsPerPage > 0 && (rowsApiPreload || rows) && !handlePageChange) {
            currentRenderRows = currentRenderRows.filter(
                (_r, index) =>
                    index >= rowsPerPage * page &&
                    index < rowsPerPage * (page + 1)
            );
        }

        setRenderCount(currentRenderCount);
        setRenderRows(currentRenderRows);
    }, [
        handleRows,
        handlePageChange,
        handleSelectAll,
        page,
        preprocessRows,
        propsRowCount,
        rowData,
        rows,
        rowsApiPreload,
        rowsPerPage,
        searchEnabled,
        searchFields,
        searchText,
        setRenderCount,
        setRenderRows,
        skipSorting,
        sortState
    ]);

    const buildQueryKey = useCallback(
        (dataParams, count) => {
            const queryArray = [];
            if (dataParams) {
                const dataParamsUpdated = { ...dataParams };
                if (count) {
                    delete dataParamsUpdated.page;
                    delete dataParamsUpdated.size;
                    delete dataParamsUpdated.sort;
                    delete dataParamsUpdated.order;
                    dataParamsUpdated.count = true;
                }
                if (clientId) {
                    dataParamsUpdated.clientId = clientId;
                }
                if (partnerId) {
                    dataParamsUpdated.partnerId = partnerId;
                }
                Object.keys(dataParamsUpdated).forEach((key) => {
                    let value = dataParamsUpdated[key];
                    if (Helpers.isArrayString(value)) {
                        value = Helpers.toSortedArrayString(value);
                    }
                    const keyValuePair = `${key}=${value}`;
                    if (!queryArray.includes(keyValuePair)) {
                        queryArray.push(keyValuePair);
                    }
                });
            }

            return queryArray.sort();
        },
        [clientId, partnerId]
    );

    const setupQueryKey = useCallback(
        (count) => {
            const dataParams = dataAPI?.current?.getParams();

            const queryArray = buildQueryKey(dataParams, count);

            const queryKeyUpdated = [rowsApi, ...queryArray];

            if (count) {
                setQueryCountKey(queryKeyUpdated);
            } else {
                setQueryDataKey(queryKeyUpdated);
                setCachedData(null);
            }
            if (!rows) setRowData(null);
        },
        [buildQueryKey, rows, rowsApi]
    );

    useEffect(() => {
        const checkForNewData = async () => {
            if (
                cachedData &&
                dataAPI.current &&
                isStale &&
                !newDataAvailable &&
                !newDataLoading
            ) {
                const response = await dataAPI.current.getResults();
                if (
                    response.status !== 'canceled' &&
                    response.status !== 'error' &&
                    !Helpers.arraysEqual(response, cachedData)
                ) {
                    setNewDataAvailable(true);
                }
            }
        };
        checkForNewData();
        setIsStale(false);
    }, [cachedData, isStale, newDataAvailable, newDataLoading]);

    useEffect(() => {
        setApiLoading(!rows && !cachedData);
        const data = queryClient.getQueryData(queryDataKey);

        // Invalidating the query will force the function to run again. We need to make sure
        // that we're not in the middle of getting some data or else we will cancel and recall
        // the function indefinitely.
        if (data?.status === 'canceled' && !queryData.isFetching) {
            queryClient.invalidateQueries(queryDataKey);
        } else if (data && !cachedData) {
            setIsStale(true);
            setCachedData(data);
        }
    }, [
        cachedData,
        queryClient,
        queryData.isFetching,
        queryDataKey,
        rows,
        setApiLoading,
        setCachedData
    ]);

    useEffect(() => {
        if (!skipCountRows && !rowsApiPreload && countAPI.current) {
            const count = queryClient.getQueryData(queryCountKey);
            if (count?.status === 'canceled' || count?.status === 'error') {
                queryClient.invalidateQueries(queryDataKey);
            }
            setRowCount(count);
        }
    }, [
        queryClient,
        queryCountKey,
        queryDataKey,
        rowCount,
        rowsApiPreload,
        setRowCount,
        skipCountRows
    ]);

    const setupApiCall = useCallback(
        ({ countUnaffected = false }) => {
            if (!rowsApi) {
                debug.i('rowsApi is undefined', rowsApi);
                return;
            }

            if (!dataAPI.current) {
                debug.i('dataAPI has not been initialized');
                return;
            }

            cancelDataApis({
                cancelCountApi: !countUnaffected,
                cancelDataApi: !!apiTable
            }); // cancel countAPI only if count is affected

            setupDataAPI({
                apiSize,
                clientId,
                dataAPI: dataAPI.current,
                filters,
                page,
                parameters,
                rowsApi,
                rowsApiPreload,
                rowsPerPage,
                sortState,
                toggles
            });

            // Set up the query key for the data
            setupQueryKey();
            if (!rowsApiPreload && !countUnaffected && dataAPI.current) {
                // Set up the query key for the count
                setupQueryKey(true);
            }
            setSkipCountRows(countUnaffected);
        },
        [
            apiSize,
            apiTable,
            cancelDataApis,
            clientId,
            filters,
            page,
            parameters,
            rowsApi,
            rowsApiPreload,
            rowsPerPage,
            setupQueryKey,
            sortState,
            toggles
        ]
    );

    const handleRowApi = useCallback(async () => {
        if (queryData.isPending) return;
        // Don't set the rowCount if we are getting one page at a time from the backend
        if (rowsApiPreload) {
            setRowCount(Helpers.count(cachedData));
        }
        // }

        /* Get the response, then send the dataAPI data up to the parent component
                if anything needs rendered with it */
        if (setDataFn) {
            setDataFn(cachedData);
        }

        if (handleRows) {
            handleRows(cachedData);
        }

        // status is only set to canceled or error in DataAPI.js
        if (cachedData?.status !== 'error') {
            setRowData(cachedData);
        }

        setApiLoading(false);
        setSkipCountRows(true);
    }, [
        cachedData,
        handleRows,
        queryData.isPending,
        rowsApiPreload,
        setApiLoading,
        setDataFn,
        setRowCount,
        setRowData
    ]);

    useEffect(() => {
        if (
            !queryData.isLoading &&
            !rows &&
            (cachedData?.length ||
                (cachedData?.length === 0 && queryData.data?.length === 0))
        ) {
            handleRowApi();
        }

        // Don't leave the skeleton rows hanging
        if (cachedData?.status === 'error' && !rowData) {
            setRowData([]);
            setApiLoading(false);
            setSkipCountRows(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cachedData]);

    useEffect(() => {
        setFilters(setupFilters(propsHeaders));
    }, [propsHeaders, setFilters]);

    useEffect(() => {
        if (rows && !rowsApi) {
            setRowData(rows);
            setRowCount(rows.length);
        }
    }, [rows, rowsApi, setRowCount, setRowData]);

    useEffect(() => {
        handleRenderRows();
    }, [handleRenderRows, rowData, searchText]);

    useEffect(() => {
        if (!dataAPI.current && !!rowsApi) {
            dataAPI.current = new DataAPI({
                route: rowsApi?.split('?')[0]
            });
            countAPI.current = rowsApi
                ? new DataAPI(dataAPI?.current?.getConfig())
                : null;
        }
        if (apiTable || Object.keys(toggles)?.length > 0) {
            if (queryData?.isLoading) {
                queryClient.removeQueries({ queryKey: queryDataKey });
            }
            if (queryRowCount?.isLoading) {
                queryClient.removeQueries({ queryKey: queryCountKey });
            }
            setupApiCall({
                countUnaffected: false // Total row count unaffected by filters change
            });
        }

        setSearchText(defaultSearch ? defaultSearch.toLowerCase() : ''); // Reset search
        setPage(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, parameters, rowsApi, toggles]);

    useEffect(() => {
        if (!queryClient.isLoading) {
            if (apiTable && !rowsApiPreload) {
                setupApiCall({ countUnaffected: true });
            } else {
                handleRenderRows();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, sortState]);

    useEffect(() => {
        if (rerender && apiTable) {
            setupApiCall({ countUnaffected: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rerender]);

    useEffect(() => {
        if (propsHeaders) {
            setupHeaders(propsHeaders);
        }
    }, [propsHeaders]);

    useEffect(() => {
        if (handlePageChange) {
            handlePageChange(page);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    useEffect(() => {
        if (handleRowsPerPage) {
            handleRowsPerPage(rowsPerPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowsPerPage]);

    const onRefresh = async () => {
        setNewDataAvailable(false);
        setNewDataLoading(true);
        await queryClient.resetQueries([rowsApi]);
    };

    const showLoadingState =
        (!cachedData && Boolean(rowsApi)) ||
        (!rowData && Boolean(rowsApi)) ||
        externalLoading ||
        apiLoading ||
        !renderRows;

    if (!renderRows?.length && noResultsRender) {
        return noResultsRender;
    }

    const showToggles = !!Object.keys(toggles)?.length;

    const disabled = !rowData;

    return (
        <Box
            data-trackid="managedTableRenderBoxContainer"
            data-testid="managedTableRenderBoxContainer"
            {...(showLoadingState ? { className: 'no-print' } : {})}
        >
            <Box width="100%">
                {(searchEnabled ||
                    csvExport ||
                    newDataAvailable ||
                    newDataLoading) && (
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-start"
                        flexWrap="wrap"
                        gap="16px"
                        p="16px"
                        pl="0"
                    >
                        {searchEnabled && (
                            <Box sx={{ maxWidth: '560px', width: '560px' }}>
                                <ManagedTableSearchBar
                                    disabled={disabled}
                                    externalLoading={
                                        disabled || externalLoading
                                    }
                                    searchText={searchText}
                                    setPage={setPage}
                                    setSearchText={setSearchText}
                                />
                            </Box>
                        )}
                        {csvExport && (
                            <ManagedTableCSVExport
                                csvBuildOnRequest={csvBuildOnRequest}
                                csvData={csvData}
                                csvExport={csvExport}
                                csvExportFields={csvExportFields}
                                csvExportFileName={csvExportFileName}
                                csvIconOnly={csvIconOnly}
                                data-trackid={dataTrackid}
                                dataAPI={dataAPI.current}
                                disabled={disabled}
                                maxExportRows={maxExportRows}
                                rowCount={rowCount}
                                rowData={rowData}
                            />
                        )}
                        {(newDataAvailable || newDataLoading) && (
                            <ManagedTableNewData
                                newDataAvailable={newDataAvailable}
                                newDataLoading={newDataLoading}
                                onRefresh={() => onRefresh()}
                            />
                        )}
                    </Box>
                )}
                {showToggles && (
                    <ManagedTableToggles
                        disabled={disabled || externalLoading}
                        setPage={setPage}
                        setSearchText={setSearchText}
                        setToggles={setToggles}
                        toggles={toggles}
                    />
                )}
            </Box>
            <TableContainer data-trackid="managedTableRenderTableContainer">
                <Table
                    aria-label="simple table"
                    className={className}
                    data-testid="managedTableRenderTable"
                    size="small"
                    sx={{
                        border: '1px solid',
                        borderColor: 'table.border',
                        borderRadius: '4px',
                        width: width || '100%'
                    }}
                    variant={alternateRows ? 'alternateRows' : 'standard'}
                >
                    {renderHeaderRow !== false && (
                        <ManagedTableHeaders
                            disabled={showLoadingState}
                            filters={filters}
                            handleSelectAll={handleSelectAll}
                            headers={headers}
                            setFilters={setFilters}
                            setPage={setPage}
                            setSortState={setSortState}
                            sortState={sortState}
                        />
                    )}
                    {showLoadingState ? (
                        <TableBody>
                            <ManagedTableSkeletonRows
                                colSpan={colSpan}
                                rowHeight={rowHeight}
                                rowsPerPage={rowsPerPage}
                            />
                        </TableBody>
                    ) : (
                        <ManagedTableRows
                            colSpan={colSpan}
                            emptyRowsMessage={emptyRowsMessage}
                            manageRows={manageRows}
                            renderBody={renderBody}
                            renderBodyKey={renderBodyKey}
                            renderRows={renderRows}
                            rowHeight={rowHeight}
                            searchEnabled={searchEnabled}
                            searchText={searchText}
                        />
                    )}
                    {!!renderFooter && (
                        <ManagedTableFooter
                            disableFirstAndLastPageButtons={
                                disableFirstAndLastPageButtons
                            }
                            apiTable={apiTable}
                            data-trackid={dataTrackid}
                            disabled={showLoadingState}
                            filters={filters}
                            footer={footer}
                            isSearching={!!searchText}
                            page={page}
                            renderCount={renderCount}
                            rowCount={rowCount}
                            rowsApiPreload={rowsApiPreload}
                            rowsPerPage={rowsPerPage}
                            setFilters={setFilters}
                            setPage={setPage}
                            setRowsPerPage={setRowsPerPage}
                            rowData={rowData}
                        />
                    )}
                </Table>
            </TableContainer>
        </Box>
    );
};
